import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { action, computed, makeObservable, observable, ObservableMap } from "mobx";
import { api } from "@/modules/api";
import type { ApiSchema } from "@/modules/api";
import { logger } from "@/modules/logger";
import { toastModule } from "@/modules/toast";
import { SmartViewKind } from "@/domains/smart/constants";
import type { TranscriptArrayItem } from "@/modules/audio-recording/transcript-types";
import { resolveSmartContextId } from "./SmartContextId";
import { requestMeetingBriefing } from "./meeting-briefing";
import { HeadsUpOperationKind, isBlockingContextOperation } from "./HeadsUpOperation";
import {
  HeadsUpAutoGenerator,
  HeadsUpEditorBlurPayload,
  HeadsUpTemplateInsertedPayload,
  HeadsUpEditorUpdatePayload,
} from "./HeadsUpAutoGenerator";
import { buildSmartViewPayload } from "./impl/build-smart-view-payload";
import { runDedupedViewOperation } from "./impl/run-deduped-view-operation";

export interface SmartContextRequest {
  activeNoteId?: string | null;
  query?: string | null;
  smartViewKind?: SmartViewKind | null;
}

type HeadsUpInFlightEntry = {
  kind: Exclude<HeadsUpOperationKind, HeadsUpOperationKind.Idle>;
  promise: Promise<ApiSchema["SmartViewResponseSchema"] | null>;
  smartViewId?: string | null;
};

type HeadsUpContextOperationLane = "live" | "non-live";

type HeadsUpInFlightByContextLane = {
  live?: HeadsUpInFlightEntry;
  "non-live"?: HeadsUpInFlightEntry;
};

type SmartViewResponse = ApiSchema["SmartViewResponseSchema"];
type SmartCardResponse = SmartViewResponse["smart_cards"][number];

function buildSmartCardUpsertInput({
  card,
  data,
  cardTimestamp,
}: {
  card: SmartCardResponse;
  data: SmartViewResponse;
  cardTimestamp: string;
}) {
  const cardValue = (("value" in card ? card.value : null) ?? {}) as Record<string, unknown>;
  const rawTitle = cardValue.title;
  const derivedInternalLabel =
    (typeof rawTitle === "string" && rawTitle.trim() ? rawTitle : null) ?? card.kind;
  const sortKey = ("sort_key" in card ? card.sort_key : null) ?? null;

  return {
    id: card.id,
    associated_space_account_id: data.associated_space_account_id,
    smart_context_id: data.smart_context_id,
    smart_view_id: card.smart_view_id,
    sort_key: sortKey,
    added_by_space_account_id: null,
    derived_kind: card.kind ?? null,
    derived_internal_label: derivedInternalLabel,
    originated_from_find_more:
      ("originated_from_find_more" in card ? card.originated_from_find_more : null) ?? false,
    value_json: cardValue,
    refreshed_at: cardTimestamp,
    created_at: cardTimestamp,
    updated_at: cardTimestamp,
  };
}

export class AppStoreHeadsUpStore extends AppSubStore {
  operationByContextId = new ObservableMap<string, HeadsUpOperationKind>();
  operationByViewId = new ObservableMap<string, HeadsUpOperationKind>();
  errorByContextId = new ObservableMap<string, string | null>();
  activeContextId: string | null = null;
  autoGenerationDeferredByContextId = new ObservableMap<string, boolean>();
  private inFlightByContextId = new Map<string, HeadsUpInFlightByContextLane>();
  private inFlightByViewId = new Map<string, HeadsUpInFlightEntry>();
  private readonly autoGenerator: HeadsUpAutoGenerator;
  findMoreByViewId = new ObservableMap<string, boolean>();
  private inFlightFindMoreByViewId = new Map<
    string,
    Promise<ApiSchema["SmartViewResponseSchema"] | null>
  >();

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    this.autoGenerator = new HeadsUpAutoGenerator(this);

    makeObservable<
      this,
      | "resolveContextIdFrom"
      | "persistFromResponse"
      | "runOperation"
      | "inFlightByContextId"
      | "inFlightByViewId"
      | "inFlightFindMoreByViewId"
      | "setOperationState"
      | "getContextOperationLane"
      | "clearOperationState"
      | "setAutoGenerationDeferredState"
      | "autoGenerator"
    >(this, {
      hydrateContext: true,
      operationByContextId: observable,
      operationByViewId: observable,
      errorByContextId: observable,
      activeContextId: observable,
      autoGenerationDeferredByContextId: observable,
      autoGenerator: false,
      inFlightByContextId: false,
      inFlightByViewId: false,
      findMoreByViewId: observable,
      inFlightFindMoreByViewId: false,
      anyGenerating: computed,
      isHydratingForContextId: false,
      isGeneratingForContextId: false,
      isRefreshingForViewId: false,
      isRegeneratingForViewId: false,
      isFindingMoreForViewId: false,
      isBusyForContextId: false,
      getErrorForContextId: false,
      setActiveContextId: action,
      setOperationState: action,
      getContextOperationLane: false,
      clearOperationState: action,
      generateForContext: action,
      requestLiveCardsForContext: action,
      requestMeetingBriefingForContext: action,
      refreshSmartView: action,
      regenerateSmartView: action,
      deferAutoGenerationUntilNoteReady: action,
      markNoteSyncedForAutoGeneration: action,
      markNoteHasPrimaryLabelForAutoGeneration: action,
      handleEditorUpdate: action,
      handleEditorBlur: action,
      handleTemplateInserted: action,
      canAutoGenerateForContextId: false,
      findMoreForSmartView: action,
      resolveContextIdFrom: false,
      runOperation: false,
      persistFromResponse: false,
      setAutoGenerationDeferredState: action,
      resolveContextIdForRequest: false,
      isAutoGenerationReadyForContextId: false,
    });
  }

  get anyGenerating(): boolean {
    for (const value of this.operationByContextId.values()) {
      if (value === HeadsUpOperationKind.Generate) return true;
    }
    return false;
  }

  isGeneratingForContextId = (contextId: string | null): boolean => {
    if (!contextId) return false;
    return this.operationByContextId.get(contextId) === HeadsUpOperationKind.Generate;
  };

  isHydratingForContextId = (contextId: string | null): boolean => {
    if (!contextId) return false;
    return this.operationByContextId.get(contextId) === HeadsUpOperationKind.Hydrate;
  };

  isRefreshingForViewId = (viewId: string | null): boolean => {
    if (!viewId) return false;
    return this.operationByViewId.get(viewId) === HeadsUpOperationKind.Refresh;
  };

  isRegeneratingForViewId = (viewId: string | null): boolean => {
    if (!viewId) return false;
    return this.operationByViewId.get(viewId) === HeadsUpOperationKind.Regenerate;
  };

  isFindingMoreForViewId = (viewId: string | null): boolean => {
    if (!viewId) return false;
    return this.findMoreByViewId.get(viewId) === true;
  };

  isBusyForContextId = (contextId: string | null): boolean => {
    if (!contextId) return false;
    return isBlockingContextOperation(this.operationByContextId.get(contextId));
  };

  getErrorForContextId = (contextId: string | null): string | null => {
    if (!contextId) return null;
    if (this.isBusyForContextId(contextId)) return null;
    return this.errorByContextId.get(contextId) ?? null;
  };

  setActiveContextId = (contextId: string | null) => {
    this.activeContextId = contextId;
  };

  canAutoGenerateForContextId = (contextId: string | null): boolean => {
    if (!contextId) return false;
    return this.isAutoGenerationReadyForContextId(contextId);
  };

  deferAutoGenerationUntilNoteReady = (noteId: string | null) => {
    if (!noteId) return;
    this.autoGenerator.deferAutoGenerationFor({
      activeNoteId: noteId,
      smartViewKind: SmartViewKind.HeadsUp,
    });
  };

  markNoteSyncedForAutoGeneration = (noteId: string | null) => {
    if (!noteId) return;
    this.autoGenerator.markNoteSynced({
      activeNoteId: noteId,
      smartViewKind: SmartViewKind.HeadsUp,
    });
  };

  markNoteHasPrimaryLabelForAutoGeneration = (noteId: string | null) => {
    if (!noteId) return;
    this.autoGenerator.markNoteHasPrimaryLabel({
      activeNoteId: noteId,
      smartViewKind: SmartViewKind.HeadsUp,
    });
  };

  handleEditorUpdate = (payload: HeadsUpEditorUpdatePayload) => {
    this.autoGenerator.handleEditorUpdate(payload);
  };

  handleEditorBlur = (payload: HeadsUpEditorBlurPayload) => {
    this.autoGenerator.handleEditorBlur(payload);
  };

  handleTemplateInserted = (payload: HeadsUpTemplateInsertedPayload) => {
    this.autoGenerator.handleTemplateInserted(payload);
  };

  resolveContextIdForRequest = (request: SmartContextRequest): string | null => {
    return this.resolveContextIdFrom(request);
  };

  isAutoGenerationReadyForContextId = (contextId: string | null): boolean => {
    if (!contextId) return false;
    return !this.autoGenerationDeferredByContextId.has(contextId);
  };

  setAutoGenerationDeferredState(contextId: string | null, shouldDefer: boolean) {
    if (!contextId) return;

    if (shouldDefer) {
      this.autoGenerationDeferredByContextId.set(contextId, true);
    } else {
      this.autoGenerationDeferredByContextId.delete(contextId);
    }
  }

  private setOperationState({
    contextId,
    smartViewId,
    kind,
  }: {
    contextId: string | null;
    smartViewId: string | null;
    kind: Exclude<HeadsUpOperationKind, HeadsUpOperationKind.Idle>;
  }) {
    if (contextId) {
      // Context operation state only tracks blocking (non-live) lane.
      // Live operations run concurrently and should not overwrite blocking state.
      if (this.getContextOperationLane(kind) === "non-live") {
        this.operationByContextId.set(contextId, kind);
      }
    }

    if (smartViewId) {
      this.operationByViewId.set(smartViewId, kind);
    }
  }

  private getContextOperationLane(
    kind: Exclude<HeadsUpOperationKind, HeadsUpOperationKind.Idle>
  ): HeadsUpContextOperationLane {
    return kind === HeadsUpOperationKind.Live ? "live" : "non-live";
  }

  private clearOperationState({
    contextId,
    smartViewId,
    kind,
  }: {
    contextId: string | null;
    smartViewId: string | null;
    kind: Exclude<HeadsUpOperationKind, HeadsUpOperationKind.Idle>;
  }) {
    if (contextId) {
      const lane = this.getContextOperationLane(kind);

      if (lane === "non-live") {
        this.operationByContextId.delete(contextId);
      }

      const entries = this.inFlightByContextId.get(contextId);
      if (entries) {
        delete entries[lane];
        if (!entries.live && !entries["non-live"]) {
          this.inFlightByContextId.delete(contextId);
        } else {
          this.inFlightByContextId.set(contextId, entries);
        }
      }
    }

    if (smartViewId) {
      this.operationByViewId.delete(smartViewId);
      this.inFlightByViewId.delete(smartViewId);
    }
  }

  private runOperation({
    contextId,
    smartViewId,
    kind,
    executor,
  }: {
    contextId: string | null;
    smartViewId: string | null;
    kind: Exclude<HeadsUpOperationKind, HeadsUpOperationKind.Idle>;
    executor: () => Promise<ApiSchema["SmartViewResponseSchema"] | null>;
  }): Promise<ApiSchema["SmartViewResponseSchema"] | null> {
    const normalizedContextId = contextId ?? null;
    const normalizedViewId = smartViewId ?? null;

    if (normalizedContextId) {
      const lane = this.getContextOperationLane(kind);
      const existingContextEntry = this.inFlightByContextId.get(normalizedContextId)?.[lane];
      if (existingContextEntry) {
        return existingContextEntry.promise;
      }
    }

    if (normalizedViewId) {
      const existingViewEntry = this.inFlightByViewId.get(normalizedViewId);
      if (existingViewEntry) {
        return existingViewEntry.promise;
      }
    }

    const promise = (async () => {
      this.setOperationState({
        contextId: normalizedContextId,
        smartViewId: normalizedViewId,
        kind,
      });

      try {
        return await executor();
      } finally {
        this.clearOperationState({
          contextId: normalizedContextId,
          smartViewId: normalizedViewId,
          kind,
        });
      }
    })();

    const entry: HeadsUpInFlightEntry = {
      kind,
      promise,
      smartViewId: normalizedViewId,
    };

    if (normalizedContextId) {
      const lane = this.getContextOperationLane(kind);
      const existingEntries = this.inFlightByContextId.get(normalizedContextId) ?? {};
      this.inFlightByContextId.set(normalizedContextId, {
        ...existingEntries,
        [lane]: entry,
      });
    }

    if (normalizedViewId) {
      this.inFlightByViewId.set(normalizedViewId, entry);
    }

    return promise;
  }

  private resolveContextIdFrom({
    activeNoteId = null,
    query = null,
    smartViewKind = SmartViewKind.HeadsUp,
  }: SmartContextRequest): string | null {
    const spaceAccountId = this.store.spaceAccounts.myPersonalSpaceAccountId ?? null;
    return resolveSmartContextId({
      spaceAccountId,
      activeNoteId: activeNoteId ?? null,
      query: query ?? null,
      smartViewKind: smartViewKind ?? SmartViewKind.HeadsUp,
    });
  }

  async hydrateContext({
    activeNoteId = null,
    query = null,
    smartViewKind = SmartViewKind.HeadsUp,
  }: SmartContextRequest): Promise<ApiSchema["SmartViewResponseSchema"] | null> {
    const contextId = this.resolveContextIdFrom({
      activeNoteId,
      query,
      smartViewKind,
    });
    if (!contextId) return null;

    return this.runOperation({
      contextId,
      smartViewId: null,
      kind: HeadsUpOperationKind.Hydrate,
      executor: async () => {
        try {
          logger.info({
            message: "[HeadsUp] Hydration API request",
            info: { contextId },
          });
          const response = await api.get("/v2/smart-views/by-context/{smart_context_id}", {
            params: {
              path: {
                smart_context_id: contextId,
              },
            },
          });
          const data = response.data;
          if (!data) {
            return null;
          }

          const payloadJson = buildSmartViewPayload({
            spaceAccountId:
              this.store.spaceAccounts.myPersonalSpaceAccountId ?? data.associated_space_account_id,
            smartViewKind,
            activeNoteId,
            query,
          });

          await this.persistFromResponse({ data, payloadJson, replaceCards: true });

          return data;
        } catch (_error) {
          logger.debug({
            message: "[HeadsUp] Hydration request failed; falling back to generation",
            info: { contextId },
          });
          return null;
        }
      },
    });
  }

  async generateForContext({
    activeNoteId = null,
    query = null,
    smartViewKind = SmartViewKind.HeadsUp,
    suppressLoadingState = false,
  }: SmartContextRequest & {
    suppressLoadingState?: boolean;
  }): Promise<ApiSchema["SmartViewResponseSchema"] | null> {
    const spaceAccountId = this.store.spaceAccounts.myPersonalSpaceAccountId ?? null;
    if (!spaceAccountId) {
      logger.warn({
        message: "[HeadsUp] Cannot generate without space account",
      });
      return null;
    }

    const contextId = this.resolveContextIdFrom({
      activeNoteId,
      query,
      smartViewKind,
    });
    if (!contextId) return null;

    this.errorByContextId.delete(contextId);

    return this.runOperation({
      contextId,
      smartViewId: null,
      kind: suppressLoadingState ? HeadsUpOperationKind.Live : HeadsUpOperationKind.Generate,
      executor: async () => {
        try {
          logger.info({
            message: "[HeadsUp] Generate API request",
            info: {
              contextId,
              spaceAccountId,
              smartViewKind,
              hasActiveNoteId: Boolean(activeNoteId),
              hasQuery: Boolean(query),
              suppressLoadingState,
            },
          });
          const response = await api.post(
            "/v2/smart-views/by-context/{smart_context_id}/generate",
            {
              params: {
                path: {
                  smart_context_id: contextId,
                },
              },
              body: {
                context: {
                  space_account_id: spaceAccountId,
                  smart_view_kind: smartViewKind,
                  ...(activeNoteId ? { active_note_id: activeNoteId } : {}),
                  ...(query ? { query } : {}),
                },
              },
            }
          );

          const data = response.data;

          if (!data) {
            throw new Error("Failed to generate smart view");
          }

          const payloadJson = buildSmartViewPayload({
            spaceAccountId,
            smartViewKind,
            activeNoteId,
            query,
          });

          await this.persistFromResponse({ data, payloadJson, replaceCards: false });

          return data;
        } catch (error) {
          const message = error instanceof Error ? error.message : "Unknown error";
          this.errorByContextId.set(contextId, message);
          throw error;
        }
      },
    });
  }

  async requestLiveCardsForContext({
    contextId,
    transcriptText,
    transcriptArray,
    spaceAccountId,
    activeNoteId,
    recordingStartedAt,
    signal,
  }: {
    contextId: string;
    transcriptText: string;
    transcriptArray: TranscriptArrayItem[];
    spaceAccountId: string;
    activeNoteId: string | null;
    recordingStartedAt?: string | null;
    signal: AbortSignal;
  }): Promise<ApiSchema["SmartViewResponseSchema"] | null> {
    this.errorByContextId.delete(contextId);

    return this.runOperation({
      contextId,
      smartViewId: null,
      kind: HeadsUpOperationKind.Live,
      executor: async () => {
        const response = await api.post("/v2/smart-views/by-context/{smart_context_id}/live", {
          params: {
            path: { smart_context_id: contextId },
          },
          body: {
            transcript_text: transcriptText,
            transcript_array: transcriptArray,
            recording_started_at: recordingStartedAt ?? undefined,
            context: {
              space_account_id: spaceAccountId,
              smart_view_kind: SmartViewKind.HeadsUp,
              active_note_id: activeNoteId,
            },
          } as never,
          signal,
        });

        const data = response.data;
        if (!data) {
          return null;
        }

        const payloadJson = buildSmartViewPayload({
          spaceAccountId,
          smartViewKind: SmartViewKind.HeadsUp,
          activeNoteId,
          query: null,
        });

        await this.persistFromResponse({ data, payloadJson, replaceCards: false });
        return data;
      },
    });
  }

  async requestMeetingBriefingForContext({
    activeNoteId,
    connectedCalendarEventId,
    forceGenerate = false,
    forceRegenerate = false,
  }: {
    activeNoteId: string;
    connectedCalendarEventId: string;
    forceGenerate?: boolean;
    forceRegenerate?: boolean;
  }): Promise<ApiSchema["SmartViewResponseSchema"] | null> {
    const spaceAccountId = this.store.spaceAccounts.myPersonalSpaceAccountId ?? null;
    if (!spaceAccountId) {
      logger.warn({
        message: "[HeadsUp] Cannot request meeting briefing without space account",
      });
      return null;
    }

    const contextId = this.resolveContextIdFrom({
      activeNoteId,
      smartViewKind: SmartViewKind.HeadsUp,
    });
    if (!contextId) return null;

    this.errorByContextId.delete(contextId);

    return this.runOperation({
      contextId,
      smartViewId: null,
      kind: HeadsUpOperationKind.Generate,
      executor: async () => {
        try {
          const data = await requestMeetingBriefing({
            spaceAccountId,
            activeNoteId,
            contextId,
            connectedCalendarEventId,
            forceGenerate,
            forceRegenerate,
          });
          const payloadJson = buildSmartViewPayload({
            spaceAccountId,
            smartViewKind: SmartViewKind.HeadsUp,
            activeNoteId,
          });
          await this.persistFromResponse({ data, payloadJson, replaceCards: true });
          return data;
        } catch (error) {
          const message = error instanceof Error ? error.message : "Unknown error";
          this.errorByContextId.set(contextId, message);
          throw error;
        }
      },
    });
  }

  protected async persistFromResponse({
    data,
    payloadJson,
    replaceCards,
  }: {
    data: ApiSchema["SmartViewResponseSchema"];
    payloadJson: Record<string, unknown>;
    replaceCards: boolean;
  }): Promise<void> {
    const smartContextId = data.smart_context_id;

    if (this.store.db.isInitialized()) {
      try {
        logger.info({
          message: "[HeadsUp] Persisting API response locally",
          info: { smartContextId, smartViewId: data.id, replaceCards },
        });
        logger.debug({
          message: "[HeadsUp] Upserting smart context",
          info: { smartContextId },
        });

        await this.store.db.smartContexts.upsertContext({
          id: smartContextId,
          associated_space_account_id: data.associated_space_account_id,
          payload_json: payloadJson,
          created_at: data.created_at,
          updated_at: data.updated_at,
        });

        logger.debug({
          message: "[HeadsUp] Upserting smart view",
          info: { smartViewId: data.id, smartContextId },
        });

        const responseRefreshedAt = data.refreshed_at ?? null;

        await this.store.db.smartViews.upsertView({
          id: data.id,
          associated_space_account_id: data.associated_space_account_id,
          smart_context_id: data.smart_context_id,
          derived_kind: null,
          derived_internal_label: null,
          value_json: data.value_json,
          has_found_more: (data as { has_found_more?: boolean }).has_found_more ?? false,
          refreshed_at: responseRefreshedAt,
          created_at: data.created_at ?? null,
          updated_at: data.updated_at ?? null,
        });

        const smartCards = data.smart_cards ?? [];
        const cardTimestamp = responseRefreshedAt ?? new Date().toISOString();
        const smartCardRows = smartCards.map(card =>
          buildSmartCardUpsertInput({ card, data, cardTimestamp })
        );

        if (replaceCards) {
          await this.store.db.smartCards.replaceCardsForSmartView({
            smartViewId: data.id,
            rows: smartCardRows,
          });
        } else {
          for (const row of smartCardRows) {
            await this.store.db.smartCards.upsertCard(row);
          }
        }

        logger.info({
          message: "[HeadsUp] Local persistence complete",
          info: {
            smartContextId,
            smartViewId: data.id,
            smartCardCount: smartCards.length,
            replaceCards,
          },
        });
      } catch (unknownErr) {
        const err = unknownErr as Error;
        logger.warn({
          message: "[HeadsUp] Local upsert failed; relying on sync",
          info: { error: err.message, smartContextId, smartViewId: data.id },
        });
      }
    } else {
      logger.warn({
        message: "[HeadsUp] Skipping local upsert; database not initialized",
        info: { smartContextId },
      });
    }

    const activeNoteId = (payloadJson["active_note_id"] as string | undefined) ?? null;
    if (activeNoteId) {
      this.autoGenerator.noteHasHeadsUp({
        activeNoteId,
        smartViewKind: SmartViewKind.HeadsUp,
      });
    }
  }

  async refreshSmartView({
    smartViewId,
    contextId,
  }: {
    smartViewId: string;
    contextId: string | null;
  }): Promise<ApiSchema["SmartViewResponseSchema"] | null> {
    const normalizedContextId = contextId ?? null;
    if (normalizedContextId) {
      this.errorByContextId.delete(normalizedContextId);
    }

    return this.runOperation({
      contextId: normalizedContextId,
      smartViewId,
      kind: HeadsUpOperationKind.Refresh,
      executor: async () => {
        try {
          const response = await api.post("/v2/smart-views/{smart_view_id}/refresh", {
            params: {
              path: { smart_view_id: smartViewId },
            },
          });

          const data = response.data;
          if (!data) return null;

          const spaceAccountId = this.store.spaceAccounts.myPersonalSpaceAccountId ?? null;
          const payloadJson = buildSmartViewPayload({
            spaceAccountId,
            smartViewKind: null,
            activeNoteId: null,
            query: null,
          });

          await this.persistFromResponse({ data, payloadJson, replaceCards: true });
          return data;
        } catch (error) {
          const message = error instanceof Error ? error.message : "Unknown error";
          toastModule.triggerToast({ content: message });
          throw error;
        }
      },
    });
  }

  async regenerateSmartView({
    smartViewId,
    contextId,
  }: {
    smartViewId: string;
    contextId: string | null;
  }): Promise<ApiSchema["SmartViewResponseSchema"] | null> {
    const normalizedContextId = contextId ?? null;
    if (normalizedContextId) {
      this.errorByContextId.delete(normalizedContextId);
    }

    return this.runOperation({
      contextId: normalizedContextId,
      smartViewId,
      kind: HeadsUpOperationKind.Regenerate,
      executor: async () => {
        try {
          const response = await api.post("/v2/smart-views/{smart_view_id}/regenerate", {
            params: {
              path: { smart_view_id: smartViewId },
            },
          });

          const data = response.data;
          if (!data) return null;

          const spaceAccountId = this.store.spaceAccounts.myPersonalSpaceAccountId ?? null;
          const payloadJson = buildSmartViewPayload({
            spaceAccountId,
            smartViewKind: null,
            activeNoteId: null,
            query: null,
          });

          await this.persistFromResponse({ data, payloadJson, replaceCards: true });
          return data;
        } catch (error) {
          const message = error instanceof Error ? error.message : "Unknown error";
          if (normalizedContextId) this.errorByContextId.set(normalizedContextId, message);
          throw error;
        }
      },
    });
  }

  async findMoreForSmartView({
    smartViewId,
    contextId,
  }: {
    smartViewId: string;
    contextId: string | null;
  }): Promise<ApiSchema["SmartViewResponseSchema"] | null> {
    const normalizedViewId = smartViewId;
    const normalizedContextId = contextId ?? null;

    if (!normalizedViewId) {
      return null;
    }

    const promise = runDedupedViewOperation({
      viewId: normalizedViewId,
      inFlightMap: this.inFlightFindMoreByViewId,
      onStart: viewId => {
        this.findMoreByViewId.set(viewId, true);
      },
      onFinally: viewId => {
        this.findMoreByViewId.delete(viewId);
      },
      executor: async () => {
        try {
          const response = await api.post("/v2/smart-views/{smart_view_id}/find-more", {
            params: {
              path: { smart_view_id: normalizedViewId },
            },
          });

          const data = response.data;
          if (!data) return null;

          const spaceAccountId = this.store.spaceAccounts.myPersonalSpaceAccountId ?? null;
          const payloadJson = buildSmartViewPayload({
            spaceAccountId,
            smartViewKind: null,
            activeNoteId: null,
            query: null,
          });

          await this.persistFromResponse({ data, payloadJson, replaceCards: true });

          if (normalizedContextId) {
            this.errorByContextId.delete(normalizedContextId);
          }

          return data;
        } catch (error) {
          const message = error instanceof Error ? error.message : "Unknown error";
          toastModule.triggerToast({ content: message });
          throw error;
        }
      },
    });

    return promise;
  }
}
